import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref, getDownloadURL, deleteObject, uploadBytes } from 'firebase/storage';
import { db, storage } from '@/core/firebase';
import { normalizeMuscleGroups } from '@/features/workouts/api/muscleGroups';
import { normalizeExternalMedia } from './media';
import { generateThumbnailFromUrl, getThumbnailFilename } from './imageUtils';
import { useMediaUpload } from '../hooks/useMediaUpload';
import { useDraft, type DraftFormData } from '../hooks/useDraft';
import type { Exercise, Media } from '../types';
import type { ExerciseFormState } from '../components/trainer/ExerciseForm';

const blankExerciseForm: ExerciseFormState = {
  name: '',
  description: '',
  movementPattern: undefined,
  equipmentTags: [],
  targetMuscleGroups: [],
  howTo: '',
  defaultCues: '',
  defaultSets: 3,
  defaultReps: 8,
  defaultRestSec: 90,
};

export function useTrainerExercises(trainerId?: string) {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseForm, setExerciseForm] = useState<ExerciseFormState>(blankExerciseForm);
  const [exerciseMedia, setExerciseMedia] = useState<Media[]>([]);
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs to prevent circular dependency loops in auto-save effect
  // Track which upload IDs have already been processed to prevent duplicate saves
  const processedUploadIdsRef = useRef<Set<string>>(new Set());
  // Access current exerciseMedia without adding to effect dependencies
  const exerciseMediaRef = useRef<Media[]>(exerciseMedia);
  useEffect(() => {
    exerciseMediaRef.current = exerciseMedia;
  }, [exerciseMedia]);

  // Draft system for new exercises
  const {
    draft,
    hasDraft,
    hasFormData: draftHasFormData,
    addMedia: addDraftMedia,
    removeMedia: removeDraftMedia,
    saveFormData: saveDraftFormData,
    clearDraft,
    consumeDraft,
  } = useDraft({
    trainerId,
    type: 'exercise',
    enabled: !selectedExerciseId, // Only use draft when creating new exercise
  });

  // Immediate upload hook
  const {
    uploads,
    uploadFiles,
    deleteMedia: deleteMediaFromStorage,
    removeUpload,
    retryUpload,
    isUploading,
    hasFailed,
  } = useMediaUpload({
    basePath: 'exercise_media',
    trainerId: trainerId || '',
  });

  // Subscribe to trainer-specific exercises
  useEffect(() => {
    if (!trainerId) {
      console.log('[useTrainerExercises] No trainerId, skipping query');
      return;
    }
    console.log(`[useTrainerExercises] Querying exercises for trainerId: ${trainerId}`);
    const exerciseQuery = query(
      collection(db, 'exercises'),
      where('trainerId', '==', trainerId)
    );
    const unsubExercises = onSnapshot(
      exerciseQuery, 
      (snapshot) => {
        console.log(`[useTrainerExercises] Got ${snapshot.docs.length} exercises`);
        const docs = snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Exercise)
        );
        setExercises(docs);
      },
      (error) => {
        console.error('[useTrainerExercises] Query error:', error);
        setError(error.message);
      }
    );
    return () => unsubExercises();
  }, [trainerId]);

  // Sync draft media into exerciseMedia when creating new exercise
  useEffect(() => {
    if (!selectedExerciseId && draft?.media) {
      setExerciseMedia(draft.media);
    }
  }, [selectedExerciseId, draft]);

  // Auto-save form data to draft (debounced, only for new exercises)
  const formSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // Only auto-save when creating a new exercise (not editing existing)
    if (selectedExerciseId || !trainerId) return;

    // Check if form has any meaningful content to save
    const hasContent =
      exerciseForm.name.trim() ||
      exerciseForm.description.trim() ||
      exerciseForm.howTo.trim() ||
      exerciseForm.defaultCues.trim() ||
      exerciseForm.movementPattern ||
      (exerciseForm.equipmentTags && exerciseForm.equipmentTags.length > 0) ||
      (exerciseForm.targetMuscleGroups && exerciseForm.targetMuscleGroups.length > 0);

    if (!hasContent) return;

    // Debounce: wait 500ms after last change before saving
    if (formSaveTimeoutRef.current) {
      clearTimeout(formSaveTimeoutRef.current);
    }

    formSaveTimeoutRef.current = setTimeout(() => {
      // Filter out undefined values for Firestore - use empty strings for optional text fields
      const formData: DraftFormData = {
        name: exerciseForm.name || '',
        description: exerciseForm.description || '',
        howTo: exerciseForm.howTo || '',
        defaultCues: exerciseForm.defaultCues || '',
        defaultSets: exerciseForm.defaultSets || 3,
        defaultReps: exerciseForm.defaultReps || 8,
        defaultRestSec: exerciseForm.defaultRestSec || 90,
        equipmentTags: exerciseForm.equipmentTags || [],
        targetMuscleGroups: normalizeMuscleGroups(exerciseForm.targetMuscleGroups),
        // Only include movementPattern if it has a value
        ...(exerciseForm.movementPattern && { movementPattern: exerciseForm.movementPattern }),
      };
      saveDraftFormData(formData);
    }, 500);

    return () => {
      if (formSaveTimeoutRef.current) {
        clearTimeout(formSaveTimeoutRef.current);
      }
    };
  }, [exerciseForm, selectedExerciseId, trainerId, saveDraftFormData]);

  // Auto-save completed uploads to draft (for new exercises) or exercise doc (for edits)
  // CRITICAL: This effect must prevent duplicate processing to avoid the 300+ upload bug
  useEffect(() => {
    // Filter to only completed uploads that haven't been processed yet
    const completedUploads = uploads.filter(
      (u) =>
        u.status === 'completed' &&
        u.media &&
        !processedUploadIdsRef.current.has(u.id)
    );

    if (completedUploads.length === 0) return;

    // IMMEDIATELY mark these uploads as processed to prevent re-processing
    // This must happen synchronously before any async operations
    const idsToProcess = completedUploads.map((u) => u.id);
    idsToProcess.forEach((id) => processedUploadIdsRef.current.add(id));

    const newMedia = completedUploads.map((u) => u.media!);

    // Deduplicate: filter out any media that already exists (by mediaId)
    const existingMediaIds = new Set(
      exerciseMediaRef.current.map((m) => m.mediaId)
    );
    const uniqueNewMedia = newMedia.filter(
      (m) => !existingMediaIds.has(m.mediaId)
    );

    if (uniqueNewMedia.length === 0) {
      // All media already exists, just clean up the upload queue
      idsToProcess.forEach((id) => removeUpload(id));
      return;
    }

    const saveMedia = async () => {
      try {
        if (selectedExerciseId) {
          // Editing existing exercise - update immediately
          // Use ref to get current media without dependency
          const allMedia = [...exerciseMediaRef.current, ...uniqueNewMedia];
          await updateDoc(doc(db, 'exercises', selectedExerciseId), {
            media: allMedia,
            updatedAt: Timestamp.now(),
          });
          setExerciseMedia(allMedia);
        } else {
          // New exercise - save to draft
          await addDraftMedia(uniqueNewMedia);
        }
      } catch (error) {
        console.error('Failed to save media:', error);
        // On error, remove from processed set so it can be retried
        idsToProcess.forEach((id) => processedUploadIdsRef.current.delete(id));
        return;
      }

      // Clear completed uploads from the upload queue
      idsToProcess.forEach((id) => removeUpload(id));
    };

    saveMedia();
    // Note: exerciseMedia intentionally excluded - we use exerciseMediaRef to avoid circular deps
  }, [uploads, selectedExerciseId, addDraftMedia, removeUpload]);

  const exerciseLookup = useMemo(() => {
    const map = new Map<string, Exercise>();
    exercises.forEach((exercise) => map.set(exercise.id, exercise));
    return map;
  }, [exercises]);

  const filteredExercises = useMemo(() => {
    console.log(`[useTrainerExercises] filteredExercises memo: exercises.length=${exercises.length}, search="${exerciseSearch}"`);
    if (!exerciseSearch.trim()) return exercises;
    const queryText = exerciseSearch.toLowerCase();
    return exercises.filter((exercise) =>
      `${exercise.name} ${exercise.description} ${exercise.defaultCues} ${exercise.howTo || ''}`
        .toLowerCase()
        .includes(queryText)
    );
  }, [exerciseSearch, exercises]);

  // Backfill thumbnail for a single media item (runs in background)
  const backfillThumbnail = useCallback(
    async (media: Media): Promise<Media | null> => {
      if (!trainerId || !media.downloadUrl || !media.storagePath) return null;
      if (media.type !== 'image') return null;

      try {
        // Generate thumbnail from the existing image URL
        const thumbnailBlob = await generateThumbnailFromUrl(media.downloadUrl);
        if (!thumbnailBlob) return null;

        // Extract filename from storage path
        const originalFilename = media.storagePath.split('/').pop() || 'image.jpg';
        const thumbFilename = getThumbnailFilename(originalFilename);
        const thumbnailPath = `exercise_media/${trainerId}/thumbs/${media.mediaId}-${thumbFilename}`;
        const thumbRef = ref(storage, thumbnailPath);

        // Upload thumbnail
        await uploadBytes(thumbRef, thumbnailBlob);
        const thumbnailUrl = await getDownloadURL(thumbRef);

        // Return updated media object
        return {
          ...media,
          thumbnailPath,
          thumbnailUrl,
        };
      } catch (error) {
        console.error('Thumbnail backfill failed:', error);
        return null;
      }
    },
    [trainerId]
  );

  // Update exercise document with backfilled thumbnails
  const saveBackfilledThumbnails = useCallback(
    async (exerciseId: string, updatedMedia: Media[]) => {
      try {
        await updateDoc(doc(db, 'exercises', exerciseId), {
          media: updatedMedia,
          updatedAt: Timestamp.now(),
        });
      } catch (error) {
        console.error('Failed to save backfilled thumbnails:', error);
      }
    },
    []
  );

  const hydrateMediaUrls = async (media: Media[]) => {
    const needsUrl = media.filter((m) => !m.downloadUrl);
    if (needsUrl.length === 0) return media;
    const resolved = await Promise.all(
      media.map(async (item) => {
        if (item.downloadUrl || item.externalUrl) {
          return { ...item, downloadUrl: item.downloadUrl || item.externalUrl };
        }
        try {
          const url = await getDownloadURL(ref(storage, item.storagePath));
          return { ...item, downloadUrl: url };
        } catch {
          return item;
        }
      })
    );
    return resolved;
  };

  // Trigger background thumbnail backfill for images missing thumbnails
  const triggerThumbnailBackfill = useCallback(
    (exerciseId: string, media: Media[]) => {
      const imagesToBackfill = media.filter(
        (m) => m.type === 'image' && m.downloadUrl && m.storagePath && !m.thumbnailUrl
      );

      if (imagesToBackfill.length === 0) return;

      // Run backfill in background (don't await)
      (async () => {
        const results = await Promise.all(
          imagesToBackfill.map((m) => backfillThumbnail(m))
        );

        // Merge backfilled media with original
        const backfilled = results.filter((r): r is Media => r !== null);
        if (backfilled.length === 0) return;

        const updatedMedia = media.map((m) => {
          const updated = backfilled.find((b) => b.mediaId === m.mediaId);
          return updated || m;
        });

        // Save to Firestore and update local state
        await saveBackfilledThumbnails(exerciseId, updatedMedia);

        // Update local state if still viewing this exercise
        setExerciseMedia((current) => {
          // Only update if we're still looking at the same exercise
          if (current.some((c) => backfilled.some((b) => b.mediaId === c.mediaId))) {
            return current.map((m) => {
              const updated = backfilled.find((b) => b.mediaId === m.mediaId);
              return updated || m;
            });
          }
          return current;
        });
      })();
    },
    [backfillThumbnail, saveBackfilledThumbnails]
  );

  const startExerciseEdit = useCallback(async (exercise: Exercise) => {
    // Clear processed upload tracking for fresh edit session
    processedUploadIdsRef.current.clear();

    setSelectedExerciseId(exercise.id);
    setExerciseForm({
      name: exercise.name,
      description: exercise.description,
      movementPattern: exercise.movementPattern,
      equipmentTags: exercise.equipmentTags || [],
      targetMuscleGroups: normalizeMuscleGroups(exercise.targetMuscleGroups),
      howTo: exercise.howTo || '',
      defaultCues: exercise.defaultCues,
      defaultSets: exercise.defaultSets || 3,
      defaultReps: exercise.defaultReps || 8,
      defaultRestSec: exercise.defaultRestSec || 90,
    });
    const resolved = await hydrateMediaUrls(exercise.media || []);
    setExerciseMedia(resolved);

    // Trigger background thumbnail backfill for images missing thumbnails
    triggerThumbnailBackfill(exercise.id, resolved);
  }, [triggerThumbnailBackfill]);

  const resetExerciseDraft = useCallback(async (clearDraftToo = false) => {
    setSelectedExerciseId(null);
    setExerciseForm(blankExerciseForm);
    setExerciseMedia([]);
    // Clear the processed upload tracking to start fresh
    processedUploadIdsRef.current.clear();
    if (clearDraftToo) {
      await clearDraft(true); // Delete storage files too
    }
  }, [clearDraft]);

  // Handle file uploads - immediately upload and save
  const addExerciseMedia = useCallback(
    async (files: FileList | null) => {
      if (!files || !trainerId) return;
      // Start uploading immediately - the effect above handles saving
      await uploadFiles(files);
    },
    [trainerId, uploadFiles]
  );

  // Handle external links - save immediately
  const addExternalMedia = useCallback(
    async (url: string) => {
      if (!url.trim()) return;
      const normalized = normalizeExternalMedia(url, {
        createdAt: Timestamp.now(),
      });

      if (selectedExerciseId) {
        // Editing existing - update doc
        const allMedia = [...exerciseMedia, normalized];
        await updateDoc(doc(db, 'exercises', selectedExerciseId), {
          media: allMedia,
          updatedAt: Timestamp.now(),
        });
        setExerciseMedia(allMedia);
      } else {
        // New exercise - add to draft
        await addDraftMedia(normalized);
      }
    },
    [selectedExerciseId, exerciseMedia, addDraftMedia]
  );

  // Remove media - with Storage deletion
  const removeExerciseMedia = useCallback(
    async (mediaId: string, deleteFromStorage = true) => {
      const media = exerciseMedia.find((m) => m.mediaId === mediaId);

      // Delete from Storage if it's an uploaded file
      if (deleteFromStorage && media?.storagePath) {
        try {
          await deleteObject(ref(storage, media.storagePath));
        } catch (err) {
          console.error('Failed to delete from storage:', err);
        }
      }

      if (selectedExerciseId) {
        // Editing existing - update doc
        const updated = exerciseMedia.filter((m) => m.mediaId !== mediaId);
        await updateDoc(doc(db, 'exercises', selectedExerciseId), {
          media: updated,
          updatedAt: Timestamp.now(),
        });
        setExerciseMedia(updated);
      } else {
        // New exercise - remove from draft
        await removeDraftMedia(mediaId, false); // Already deleted from storage above
      }
    },
    [selectedExerciseId, exerciseMedia, removeDraftMedia]
  );

  const saveExercise = async () => {
    if (!trainerId) {
      setError('Missing trainer id in auth claims.');
      return;
    }
    if (!exerciseForm.name?.trim()) {
      setError('Exercise name is required.');
      return;
    }
    setError(null);

    try {
      // Filter out undefined values for Firestore - use empty strings for optional text fields
      const cleanedForm = {
        name: exerciseForm.name.trim(),
        description: exerciseForm.description || '',
        howTo: exerciseForm.howTo || '',
        defaultCues: exerciseForm.defaultCues || '',
        defaultSets: exerciseForm.defaultSets || 3,
        defaultReps: exerciseForm.defaultReps || 8,
        defaultRestSec: exerciseForm.defaultRestSec || 90,
        targetMuscleGroups: normalizeMuscleGroups(exerciseForm.targetMuscleGroups),
        // Only include these if they have values
        ...(exerciseForm.movementPattern && { movementPattern: exerciseForm.movementPattern }),
        ...(exerciseForm.equipmentTags && exerciseForm.equipmentTags.length > 0 && { equipmentTags: exerciseForm.equipmentTags }),
      };

      if (selectedExerciseId) {
        // Updating existing exercise - media is already saved
        await updateDoc(doc(db, 'exercises', selectedExerciseId), {
          ...cleanedForm,
          updatedAt: Timestamp.now(),
        });
        await resetExerciseDraft(false);
        return;
      }

      // Creating new exercise - consume draft and save
      const draftData = await consumeDraft();
      const mediaToSave = draftData?.media || exerciseMedia;

      await addDoc(collection(db, 'exercises'), {
        ...cleanedForm,
        trainerId,
        media: mediaToSave,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      await resetExerciseDraft(false);
    } catch (err) {
      console.error('Failed to save exercise:', err);
      setError(`Failed to save exercise: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const setItemFromExercise = (exercise: Exercise) => {
    return {
      exerciseId: exercise.id,
      sets: exercise.defaultSets || 3,
      reps: exercise.defaultReps || 8,
      restSec: exercise.defaultRestSec || 90,
      weightSuggestion: '',
      customCues: exercise.defaultCues || '',
    };
  };

  const removeExercise = async (id: string) => {
    // Also delete all media from storage
    const exercise = exercises.find((e) => e.id === id);
    if (exercise?.media) {
      await Promise.all(
        exercise.media
          .filter((m) => m.storagePath)
          .map((m) =>
            deleteObject(ref(storage, m.storagePath!)).catch((err) =>
              console.error('Failed to delete media:', err)
            )
          )
      );
    }
    await deleteDoc(doc(db, 'exercises', id));
  };

  // Recover draft on page load (for new exercises)
  const recoverDraft = useCallback(() => {
    if (draft && !selectedExerciseId) {
      // Restore media
      setExerciseMedia(draft.media);

      // Restore form data if present
      if (draft.formData) {
        setExerciseForm({
          name: draft.formData.name || '',
          description: draft.formData.description || '',
          movementPattern: draft.formData.movementPattern,
          equipmentTags: draft.formData.equipmentTags || [],
          targetMuscleGroups: normalizeMuscleGroups(draft.formData.targetMuscleGroups),
          howTo: draft.formData.howTo || '',
          defaultCues: draft.formData.defaultCues || '',
          defaultSets: draft.formData.defaultSets ?? 3,
          defaultReps: draft.formData.defaultReps ?? 8,
          defaultRestSec: draft.formData.defaultRestSec ?? 90,
        });
      }

      return true;
    }
    return false;
  }, [draft, selectedExerciseId]);

  return {
    // Media management (new API)
    addExerciseMedia,
    addExternalMedia,
    removeExerciseMedia,
    uploads, // For showing upload progress in UI
    isUploading,
    hasFailed,
    retryUpload,
    removeUpload,
    deleteMediaFromStorage,

    // Draft management
    hasDraft,
    draftHasFormData,
    recoverDraft,
    clearDraft,

    // Legacy API (maintained for compatibility)
    error,
    exerciseForm,
    exerciseLookup,
    exerciseMedia,
    exercises,
    exerciseSearch,
    filteredExercises,
    removeExercise,
    resetExerciseDraft,
    saveExercise,
    selectedExerciseId,
    setExerciseForm,
    setExerciseSearch,
    setItemFromExercise,
    startExerciseEdit,

    // Legacy aliases (deprecated but kept for compatibility)
    addPendingExerciseMedia: addExerciseMedia,
    pendingMedia: [] as never[], // No longer used
    removePendingMedia: () => {}, // No longer used
    uploadingMedia: isUploading,
  };
}
